import {
	Avatar,
	Box,
	Grow,
	ListItemIcon,
	MenuItem,
	Popper,
	SvgIcon,
	type SxProps,
	type Theme,
} from '@mui/material';
import { Link } from 'found';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { palette, spaces } from '../../assets/styles/themes/tokens';
import { routes, zIndex } from '../../constants';
import { selectUser } from '../../reducers/user.selectors';
import { PersonalDetailsSection } from '../../routes/PersonalDetailsPage/PersonalDetailsPage';
import getMuiIcon from '../../utils/getMuiIcon';
import Logout from '../Logout';
import Menu from '../Menu/Menu';

const ProfileAvatar = () => {
	const profileAnchorEl = useRef<HTMLDivElement>(null);
	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
	const { t } = useTranslation();
	const user = useSelector(selectUser);

	const menuItemSx: SxProps<Theme> = {
		gap: 0,
		'&:hover': {
			color: 'unset',
		},
	};

	const listItemIconSx: SxProps<Theme> = {
		color: palette.text.secondary,
	};

	return (
		<>
			<Avatar
				ref={profileAnchorEl}
				sx={{
					cursor: 'pointer',
					color: palette.secondary.light,
					backgroundColor: palette.background.paperElevation0,
				}}
				onClick={function toggleMenu() {
					setIsMenuOpen(!isMenuOpen);
				}}
				aria-label="profile-avatar"
				aria-haspopup="menu"
				aria-expanded={isMenuOpen}
				aria-controls={isMenuOpen ? 'profile-menu' : undefined}
				role="button"
				data-testid="profile-avatar"
			>
				<SvgIcon component={getMuiIcon('user')} />
			</Avatar>
			<Popper
				open={isMenuOpen}
				anchorEl={profileAnchorEl.current}
				placement="bottom-end"
				transition
				// this can/should be removed later, as we won't have any topbar that would block this
				sx={{
					zIndex: `${zIndex.addMenu} !important`,
				}}
				onKeyDown={(e) => {
					if (e.key === 'Escape') {
						setIsMenuOpen(false);
					}
				}}
			>
				{({ TransitionProps }) => (
					<Grow {...TransitionProps}>
						<Box id="profile-menu">
							<Menu
								isOpen={isMenuOpen}
								onClose={() => setIsMenuOpen(false)}
								paperSx={{
									width: '220px',
									transformOrigin: 'top right',
									marginTop: spaces.xxs,
								}}
							>
								<Link
									Component={MenuItem}
									to={`/${routes.personalDetails.root}/${PersonalDetailsSection.PROFILE}`}
									childProps={{
										sx: menuItemSx,
									}}
									aria-label={t('My Profile')}
								>
									<ListItemIcon sx={listItemIconSx}>
										<SvgIcon component={getMuiIcon('profileCircle')} />
									</ListItemIcon>
									{t('My Profile')}
								</Link>
								<Link
									Component={MenuItem}
									childProps={{
										sx: menuItemSx,
									}}
									to={`/${routes.personalDetails.root}/${PersonalDetailsSection.ACCOUNT}`}
									aria-label={t('My Account')}
								>
									<ListItemIcon sx={listItemIconSx}>
										<SvgIcon component={getMuiIcon('cards')} />
									</ListItemIcon>
									{t('My Account')}
								</Link>
								{user?.isSchoolAdmin && (
									<Link
										Component={MenuItem}
										to={`/${
											user?.isStripeUser ? routes.stripeSchoolAdminTeachers.root : routes.schoolAdmin.root
										}`}
										childProps={{
											sx: menuItemSx,
										}}
										aria-label={t('Admin Area')}
									>
										<ListItemIcon sx={listItemIconSx}>
											<SvgIcon component={getMuiIcon('clipboardText')} />
										</ListItemIcon>
										{t('Admin Area')}
									</Link>
								)}
								<Logout
									render={(handleLogout) => {
										return (
											<MenuItem
												sx={menuItemSx}
												onClick={handleLogout}
												role="menuitem"
												aria-label={t('Logout')}
											>
												<ListItemIcon sx={listItemIconSx}>
													<SvgIcon component={getMuiIcon('logout')} />
												</ListItemIcon>
												{t('Logout')}
											</MenuItem>
										);
									}}
								/>
							</Menu>
						</Box>
					</Grow>
				)}
			</Popper>
		</>
	);
};

export default ProfileAvatar;
